import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SouthendOnSeaCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Southend-on-Sea Pothole Claims | 24% Estate Roads in RED | Fixtyer"
        description="Southend-on-Sea City Council reports 23.82% of unclassified roads in RED condition and only 18.26% in green — on a 379km residential network. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/southend-on-sea"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Southend-on-Sea', url: '/councils/southend-on-sea' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Southend-on-Sea has a GREEN DfT spend scorecard — can I still claim?',
            answer: 'Yes. The Spend scorecard is GREEN because the council projects £8m capital spend against a £4.473m DfT allocation in 2025/26. But the overall and condition ratings are AMBER — driven by 23.82% of unclassified roads in RED condition and only 18.26% in green. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 379.1km — 83% of Southend\'s 457.3km carriageway network. At the 2024 survey, 23.82% were in RED condition and just 18.26% in green — figures that have barely moved in four years. Most pothole damage claims happen on exactly these streets, not the 33.9km of A-roads where condition is improving.',
          },
          {
            question: 'Pothole repairs fell to 1,278 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Southend\'s own report explains the drop: the council now permanently fixes potholes rather than making temporary repairs that required return visits, inflating earlier counts. The figures also exclude works where several potholes were filled in a single visit. The council still expects roughly 2,000 pothole repairs in 2025/26, and says the true number will likely be much higher.',
          },
          {
            question: 'Does Southend\'s risk-based maintenance approach affect my claim?',
            answer: 'Potentially. Southend states it does not automatically consider roads in the worst condition first, instead weighing factors such as schools, hospitals and bus routes. That is a documented policy choice — relevant when arguing whether the council had a reasonable system for knowing about defects on your specific road under Section 58.',
          },
          {
            question: 'How quickly does Southend inspect reported potholes?',
            answer: 'The council states it inspects all issues reported through MySouthend on all roads either within 24 hours or the next working day, and describes itself as one of the best authorities in the UK for inspecting reported issues. That creates a clear standard: if you reported the defect before your incident and it was not repaired, the council\'s own inspection policy is on your side.',
          },
          {
            question: 'Should I report the pothole to Southend before claiming?',
            answer: 'Yes. Reporting through MySouthend creates a dated council record — useful if the defect was reported before your incident, or if the council failed to repair it within a reasonable time. The council inspects MySouthend reports within 24 hours or the next working day. Keep your reference number and any confirmation emails.',
          },
          {
            question: 'Does Southend use the same road survey as other councils?',
            answer: 'No. Most authorities use SCANNER surveys; Southend uses Gaist surveys, which the council says achieve yearly coverage at much lower cost. From 2026/27 it will transition to the BSI PAS 2161 five-category standard. Your claim should focus on the published red/amber/green percentages and inspection records for your road, not survey methodology alone.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Southend-on-Sea City Council"
        councilSlug="southend-on-sea"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/southend-on-sea"
        headline="Southend-on-Sea Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Southend-on-Sea</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Less than one Southend street in five is in good condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Southend-on-Sea earns a <strong className="text-emerald-400">GREEN</strong> DfT spend scorecard
            and nearly doubles its capital allocation. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because on{' '}
            <strong className="text-rose-400">379km of residential streets</strong> that make up 83% of the
            network, only <strong className="text-rose-400">18.26% are in green condition</strong> and{' '}
            <strong className="text-rose-400">23.82% need maintenance</strong>, with those figures barely
            shifting in four years of council surveys.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">18.26%</div>
            <div className="text-white font-medium mt-2">Unclassified roads in green condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Against 23.82% in RED and 57.92% in amber — on 379.1km of U-roads, the largest part of
              Southend&apos;s 457.3km carriageway network.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The condition picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Four years of Gaist survey data from Southend-on-Sea City Council&apos;s own transparency report —
            A-roads improving slightly, local roads stuck
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (33.9km — 7.4% of network): slight improvement</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">9.10%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 11.12% in 2021</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">48.23%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 45.02% in 2021</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">42.67%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">broadly flat since 2021</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              The council attributes the fall in RED A-roads to its risk management process and large
              investment in resurfacing over the last five years. But A-roads are less than one-tenth of
              the network — and fewer than 43% are in good condition even here.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (44.3km — 9.7% of network): mixed picture</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">17.68%</td>
                    <td className="px-4 py-2 text-center">51.87%</td>
                    <td className="px-4 py-2 text-center">28.51%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">14.69%</td>
                    <td className="px-4 py-2 text-center">49.67%</td>
                    <td className="px-4 py-2 text-center">33.20%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">11.76%</td>
                    <td className="px-4 py-2 text-center">51.42%</td>
                    <td className="px-4 py-2 text-center">33.98%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">12.65%</td>
                    <td className="px-4 py-2 text-center font-bold">53.88%</td>
                    <td className="px-4 py-2 text-center font-bold">33.47%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED B/C roads fell from 17.68% in 2021 to 12.65% in 2024, but two-thirds of the B/C network
              remains amber or red — only 33.47% in green condition.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And this is the well-funded version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£4.47m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£8.0m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">80%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Southend projects spending nearly{' '}
              <span className="text-emerald-400">double its DfT allocation</span> on highways capital works
              in 2025/26, with £8m allocated to road and pavement plans. The DfT Spend scorecard is GREEN —
              yet the condition rating stays AMBER because residential streets are not keeping pace.
            </p>
          </div>
        </div>
      </section>

      {/* The 379km residential network */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 379km where claims actually happen
          </h2>
          <p className="text-surface-600 mb-8">
            83% of Southend&apos;s carriageway network is unclassified roads — surveyed every year, but barely improving
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">U-roads in RED</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">U-roads in amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">U-roads in green</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23.44%</td>
                  <td className="px-6 py-4 text-center">48.73%</td>
                  <td className="px-6 py-4 text-center">18.14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26.03%</td>
                  <td className="px-6 py-4 text-center">53.80%</td>
                  <td className="px-6 py-4 text-center">18.81%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">24.10%</td>
                  <td className="px-6 py-4 text-center">56.44%</td>
                  <td className="px-6 py-4 text-center">18.17%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23.82%</td>
                  <td className="px-6 py-4 text-center font-bold">57.92%</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">18.26%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The frozen green line</h3>
              <p className="text-surface-600 text-sm mb-3">
                Southend&apos;s own summary states U-roads &ldquo;have not got worse overall during the last
                4 years&rdquo; and that it has &ldquo;kept over 70% of U roads in amber and green condition.&rdquo;
                Read the numbers differently: the green share has moved from 18.14% to 18.26% — essentially flat —
                while amber has climbed from 48.73% to 57.92%.
              </p>
              <p className="text-surface-600 text-sm">
                Roughly one in four U-roads has been in RED condition throughout — about 90km of residential
                streets, estate roads and back lanes across the city.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Gaist, not SCANNER</h3>
              <p className="text-amber-800 text-sm mb-3">
                Most local authorities use SCANNER surveys. Southend uses Gaist surveys, which the council
                says achieve yearly coverage at much lower cost. From 2026/27 it will adopt the BSI PAS 2161
                five-category standard — grouping roads into new, good, average, poor and end-of-service-life
                categories instead of red, amber and green.
              </p>
              <p className="text-amber-800 text-sm">
                For claims today, the published red/amber/green percentages and your road&apos;s inspection
                history matter more than the survey brand.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Southend&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road among the 23.82% in RED condition at the last survey — and what was done about it?</li>
              <li>• If only 18.26% of U-roads are green, how does the council prioritise preventative works on yours?</li>
              <li>• Did you report the defect through MySouthend before your incident — and was it inspected within 24 hours?</li>
              <li>• Does the risk-based scheme selection process mean your street was deprioritised despite its condition?</li>
            </ul>
            <p className="text-white font-medium">
              A council that surveys annually but keeps a quarter of residential roads in RED condition
              faces a harder Section 58 argument on those streets than on its improving A-roads.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repair data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            11,293 pothole repairs in five years
          </h2>
          <p className="text-surface-600 mb-8">
            The council&apos;s own figures — and why the 2024/25 drop is not a recovery story
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,469*</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,277*</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">1,447</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">1,822</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,278</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11,293</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The permanent-fix explanation</h3>
              <p className="text-surface-600 text-sm">
                Southend states that although statistics suggest a decrease in activity, it is actually doing
                more: previously only temporary fixes were completed, leading to return visits and higher repair
                numbers. Now all potholes meeting repair standards receive permanent fixes. The council also
                notes figures only include reports specifically labelled as potholes — not works where several
                potholes were filled at the same time.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 projection</h3>
              <p className="text-amber-800 text-sm">
                Based on previous data and its Highways Inspection policy, Southend plans roughly 2,000
                pothole fills in 2025/26 — but expects the number to be much higher due to highways schemes,
                patching works and its proactive approach. The council estimates a further 2,000 potholes
                will be fixed through its capital programme this year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Risk-based approach */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The risk-based maintenance choice
          </h2>
          <p className="text-surface-600 mb-8">
            Why the DfT Best Practice scorecard is AMBER — in the council&apos;s own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We do not automatically consider roads in the worst condition first. Instead we make sure that we look at many different factors before making decisions."
              source="Southend-on-Sea City Council Local Highway Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="Even though the statistics suggest a decrease in activity, we are actually doing more. In the past only temporary fixes to potholes were completed. This led to further visits to the same location and higher repair numbers. Now that we are permanently fixing potholes, return visits are less likely to be needed."
              source="Southend-on-Sea City Council Local Highway Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this policy means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Southend follows a risk-based approach that prioritises busy areas around schools, hospitals
                and bus routes — and explicitly does not automatically target the worst-condition roads first.
                That is a documented maintenance philosophy, not a funding failure.
              </p>
              <p className="text-surface-600 text-sm">
                For claims, it means your argument may need to show the council knew about the specific
                defect — through MySouthend reports, member enquiries or its own Gaist survey data — rather
                than relying on network-wide deterioration alone.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Inspection standards</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council states it inspects all MySouthend reports on all roads either within 24 hours or
                the next working day, and believes it is &ldquo;one of the best authorities in the UK&rdquo;
                for inspecting reported issues.
              </p>
              <p className="text-amber-800 text-sm">
                That sets a clear benchmark: if you reported the pothole before your incident and it was not
                permanently repaired, the council&apos;s own stated inspection standard supports your case.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five years of highways spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital, revenue and preventative share — from the council&apos;s published transparency tables
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Financial year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend (£)</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">4,473,000</td>
                  <td className="px-4 py-3 text-center font-bold">8,000,000</td>
                  <td className="px-4 py-3 text-center">1,041,000</td>
                  <td className="px-4 py-3 text-center text-emerald-600">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">3,151,000</td>
                  <td className="px-4 py-3 text-center">6,500,000</td>
                  <td className="px-4 py-3 text-center">745,432</td>
                  <td className="px-4 py-3 text-center text-emerald-600">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">3,151,000</td>
                  <td className="px-4 py-3 text-center">4,000,000</td>
                  <td className="px-4 py-3 text-center">1,153,392</td>
                  <td className="px-4 py-3 text-center">65%</td>
                  <td className="px-4 py-3 text-center">35%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">3,151,000</td>
                  <td className="px-4 py-3 text-center">10,000,000</td>
                  <td className="px-4 py-3 text-center">1,121,663</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">2,919,000</td>
                  <td className="px-4 py-3 text-center">10,000,000</td>
                  <td className="px-4 py-3 text-center">791,121</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Resurfacing plans</h3>
              <p className="text-surface-600 text-sm mb-3">
                In 2024/25 Southend resurfaced 6km of roads and 11km of pavement. For the coming year it
                estimates 8km of road and over 15km of pavement, and plans to resurface 5 miles of
                carriageway in 2025/26 plus 10 miles of pavement across 17 schemes.
              </p>
              <p className="text-surface-600 text-sm">
                On a 457.3km road network, even ambitious resurfacing programmes touch a fraction of the
                carriageway each year — leaving the majority reliant on reactive pothole filling.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">What the GREEN spend rating reflects</h3>
              <p className="text-emerald-800 text-sm">
                Southend consistently spends well above its DfT capital allocation — from £4m in 2023/24 to
                a projected £8m in 2025/26 — and has raised its preventative maintenance share to 80%. The
                council is investing. The AMBER condition rating reflects where that investment has not yet
                changed outcomes on residential streets.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a well-funded AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Southend invests seriously — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — nearly double DfT allocation projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 80% preventative maintenance share — up from 65% in 2023/24
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Permanent pothole repairs on all defects meeting standards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> MySouthend inspection within 24 hours or next working day
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road RED condition falling from 11.12% to 9.10% since 2021
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on main roads and busier corridors. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 23.82% of U-roads in RED, only 18.26% in green
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 379.1km of residential streets where green condition has not improved in four years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Risk-based policy — worst-condition roads not automatically prioritised
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 11,293 pothole repairs in five years — defects still forming between resurfacing schemes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice scorecard — documented departure from condition-first maintenance
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and fast MySouthend inspection times, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior MySouthend reports of the same pothole — proof of actual notice with a dated council record</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the 23.82% RED rate and frozen 18% green share are your structural arguments</li>
              <li>• Whether the council&apos;s risk-based selection process deprioritised your street despite survey data</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Southend-on-Sea&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Report a pothole in Southend-on-Sea
          </h2>
          <p className="text-surface-600 mb-8">
            Reporting the defect creates a council record — useful evidence whether or not you claim
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Council reporting channels</h3>
              <p className="text-surface-600 text-sm mb-4">
                Southend inspects all issues reported through MySouthend on all roads either within 24 hours
                or the next working day. Revenue funding covers reactive defects reported through MySouthend
                and via elected members, including emergency two-hour responses where needed.
              </p>
              <ul className="text-sm space-y-2">
                <li>
                  <a
                    href="https://www.southend.gov.uk/mysouthend-3/mysouthend"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Report through MySouthend
                  </a>
                  <span className="text-surface-500"> — potholes, pavements and other highway defects</span>
                </li>
                <li>
                  <a
                    href="https://www.southend.gov.uk/downloads/file/7180/highway-inspection-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Highways Inspection policy (PDF)
                  </a>
                  <span className="text-surface-500"> — repair standards and response times</span>
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why report before you claim</h3>
              <p className="text-amber-800 text-sm mb-3">
                A dated MySouthend report with photos creates a council record the highways team must
                inspect — within 24 hours or the next working day, by the council&apos;s own standard.
              </p>
              <p className="text-amber-800 text-sm">
                If the pothole was reported before your incident and not permanently repaired, that is direct
                evidence of actual notice — stronger than arguing from network-wide condition statistics alone.
              </p>
            </div>
          </div>

          <div className="text-center">
            <a
              href="https://www.southend.gov.uk/mysouthend-3/mysouthend"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
            >
              Report a pothole to Southend-on-Sea City Council
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
            <p className="text-surface-500 text-sm mt-4">
              Keep your reference number and any confirmation emails for a future claim.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Southend-on-Sea?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road condition data cited</li>
                <li>• No MySouthend prior-report search</li>
                <li>• No risk-based maintenance argument</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 23.82% U-road RED condition documented</li>
                <li>✅ Risk-based maintenance policy cited</li>
                <li>✅ 11,293 repairs in five years referenced</li>
                <li>✅ Prior MySouthend reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Southend-on-Sea</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=southend-on-sea"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Southend-on-Sea claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Southend-on-Sea has a GREEN DfT spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The Spend scorecard is GREEN because the council projects £8m capital spend against a
                £4.473m DfT allocation in 2025/26. But the overall and condition ratings are AMBER — driven
                by 23.82% of unclassified roads in RED condition and only 18.26% in green. Section 58 turns
                on whether the specific defect that damaged your vehicle was reasonably inspected and repaired,
                not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 379.1km — 83% of Southend&apos;s 457.3km carriageway network. At the 2024
                survey, 23.82% were in RED condition and just 18.26% in green — figures that have barely
                moved in four years. Most pothole damage claims happen on exactly these streets, not the
                33.9km of A-roads where condition is improving.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 1,278 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Southend&apos;s own report explains the drop: the council now permanently fixes potholes
                rather than making temporary repairs that required return visits, inflating earlier counts.
                The figures also exclude works where several potholes were filled in a single visit. The
                council still expects roughly 2,000 pothole repairs in 2025/26, and says the true number will
                likely be much higher.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Southend&apos;s risk-based maintenance approach affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially. Southend states it does not automatically consider roads in the worst condition
                first, instead weighing factors such as schools, hospitals and bus routes. That is a documented
                policy choice — relevant when arguing whether the council had a reasonable system for knowing
                about defects on your specific road under Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How quickly does Southend inspect reported potholes?
              </h3>
              <p className="text-surface-600 text-sm">
                The council states it inspects all issues reported through MySouthend on all roads either
                within 24 hours or the next working day, and describes itself as one of the best authorities
                in the UK for inspecting reported issues. That creates a clear standard: if you reported the
                defect before your incident and it was not repaired, the council&apos;s own inspection policy
                is on your side.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Should I report the pothole to Southend before claiming?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Reporting through MySouthend creates a dated council record — useful if the defect was
                reported before your incident, or if the council failed to repair it within a reasonable time.
                The council inspects MySouthend reports within 24 hours or the next working day. Keep your
                reference number and any confirmation emails.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Southend use the same road survey as other councils?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Most authorities use SCANNER surveys; Southend uses Gaist surveys, which the council says
                achieve yearly coverage at much lower cost. From 2026/27 it will transition to the BSI PAS 2161
                five-category standard. Your claim should focus on the published red/amber/green percentages
                and inspection records for your road, not survey methodology alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.southend.gov.uk/roads-pavements/local-highway-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Southend-on-Sea City Council Local Highway Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
